"use client";

import { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { HoverGlow, trackHoverGlow } from "./_lib/HoverGlow";
import OptimizedPicture from "./_lib/OptimizedPicture";
import { usePrefersReducedMotion } from "./_lib/usePrefersReducedMotion";

gsap.registerPlugin(ScrollTrigger);

const CARD_HEIGHT = "h-[300px] md:h-[400px]";

const BENTO_CARDS = [
  {
    colSpan: "md:col-span-6",
    image: "/assets/Bento/Product.png",
    title: "Customer",
    description:
      "Continuous teacher research, accelerated by AI. Prototypes built and tested in days. Interview synthesis in minutes. Every release starts from a real customer need, not a hunch.",
  },
  {
    colSpan: "md:col-span-6",
    image: "/assets/Bento/Design.png",
    title: "Design",
    textGradient: true,
    description:
      "From rough idea to decision-ready flow in an afternoon. AI lets us explore ten directions before committing to one, and put it in front of engineering, marketing, and sales the same day.",
  },
  {
    colSpan: "md:col-span-4",
    image: "/assets/Bento/Engineering.png",
    title: "Engineering",
    textGradient: true,
    description:
      "50 engineers, 28K agent runs, 720K lines of accepted AI code last month. Cursor and Claude Code are core to how we build.",
  },
  {
    colSpan: "md:col-span-4",
    image: "/assets/Bento/Content.png",
    title: "Content",
    description:
      "Ingestion from 2 months to 1 day. Generation fully automated. Every image AI-generated.",
  },
  {
    colSpan: "md:col-span-4",
    image: "/assets/Bento/Data.png",
    title: "Data",
    description:
      "Learning analytics and experimentation at scale. Every decision backed by real usage, progress, and outcomes.",
  },
] as const;

function BentoCard({
  colSpan,
  image,
  title,
  description,
  textGradient = false,
  cardRef,
}: {
  colSpan: string;
  image: string;
  title: string;
  description: string;
  textGradient?: boolean;
  cardRef: (el: HTMLDivElement | null) => void;
}) {
  return (
    <div
      ref={cardRef}
      className={`group relative opacity-0 ${colSpan}`}
      onMouseMove={trackHoverGlow}
    >
      <div className={`relative ${CARD_HEIGHT} rounded-2xl overflow-hidden`}>
        <OptimizedPicture
          alt={`${title} team illustration`}
          src={image}
          pictureClassName="absolute inset-0"
          imgClassName="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out will-change-transform group-hover:scale-[1.05] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
          loading="lazy"
        />
        {textGradient ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[55%] bg-gradient-to-t from-[#0D004D]/50 via-[#0D004D]/25 to-transparent"
          />
        ) : null}
        <div className="absolute bottom-5 left-5 right-5 flex flex-col gap-3 z-10">
          <h3 className="text-[20px] md:text-[24px] font-semibold leading-[28px] md:leading-[32px] text-white">
            {title}
          </h3>
          <p className="text-[14px] md:text-[16px] font-normal leading-[22px] md:leading-[24px] text-white/80">
            {description}
          </p>
        </div>
      </div>
      <HoverGlow />
    </div>
  );
}

export default function AISection() {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      const grid = gridRef.current;
      const cards = cardsRef.current.filter(Boolean) as HTMLDivElement[];

      if (prefersReducedMotion) {
        if (headingRef.current) gsap.set(headingRef.current, { opacity: 1, y: 0 });
        if (cards.length) gsap.set(cards, { opacity: 1, y: 0 });
        return;
      }

      const reveal = gsap.timeline({
        scrollTrigger: {
          trigger: grid ?? sectionRef.current,
          start: "top 82%",
          toggleActions: "play none none none",
        },
      });

      if (headingRef.current) {
        gsap.set(headingRef.current, { opacity: 0, y: 24 });
        reveal.to(
          headingRef.current,
          { opacity: 1, y: 0, duration: 0.55, ease: "power2.out" },
          0
        );
      }

      if (cards.length > 0) {
        gsap.set(cards, { opacity: 0, y: 32 });
        reveal.to(
          cards,
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.07,
            ease: "power2.out",
          },
          headingRef.current ? 0.12 : 0
        );
      }
    }, sectionRef.current);

    return () => ctx.revert();
  }, [prefersReducedMotion]);

  return (
    <section
      ref={sectionRef}
      id="ai"
      className="relative w-full pt-32 pb-12 md:py-[120px] overflow-hidden"
    >
      <div className="max-w-[1200px] mx-auto px-5">
        <h2
          ref={headingRef}
          className="text-[28px] md:text-[40px] font-semibold leading-[36px] md:leading-[48px] mb-6 md:mb-[36px] bg-clip-text text-transparent opacity-0"
          style={{
            backgroundImage:
              "linear-gradient(180deg, #ffffff 0%, #b7a8ea 100%)",
          }}
        >
          A.I. in everything we do
        </h2>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {BENTO_CARDS.map((card, i) => (
            <BentoCard
              key={card.title}
              {...card}
              cardRef={(el) => {
                cardsRef.current[i] = el;
              }}
            />
          ))}
        </div>

        <p className="mt-10 md:mt-12 mx-auto text-center text-[14px] md:text-[15px] leading-[22px] text-white/80 max-w-[640px]">
          Also, this page was designed and built with Cursor A.I. by the
          Language+ design team.
        </p>
      </div>
    </section>
  );
}
